import * as React from 'react'

import { Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import { sortBy } from 'lodash'

import { observer } from 'mobx-react'

import BoxList from 'modules/core/components/BoxList'
import PanesHeader from 'modules/core/components/PanesHeader'
import i18n from 'modules/core/lib/i18n'

import type EmployeeModel from 'modules/employees/store/models/Employee'
import AllowanceIncidenceModal from 'modules/timeoff/components/AllowanceIncidenceModal'
import { type AllowanceIncidences as AllowanceIncidencesType } from 'modules/timeoff/store/collections/allowanceIncidences'
import { type Allowances } from 'modules/timeoff/store/collections/allowances'
import type { PolicyTimelineItem } from 'modules/timeoff/store/models/TimeoffPolicy/PolicyTimeline'

import Item from './Item'

interface Props {
  employee: EmployeeModel
  timelineItem?: PolicyTimelineItem
  hasManualAdjustmentOptions: boolean
  allowanceIncidences: AllowanceIncidencesType
  allowances: Allowances
}

const AllowanceIncidences = ({
  employee,
  timelineItem,
  hasManualAdjustmentOptions,
  allowanceIncidences,
  allowances,
}: Props) => {
  const canSetAllowanceIncidences = employee.policy.can('manageIncidences')
  const [open, toggleModal] = useToggle(false)
  const hasAllowanceIncidences = allowanceIncidences.models.length > 0

  const filteredAllowances = allowances.models.filter(
    (a) => a.attributes.timeoff_policy_id === timelineItem?.timeoff_policy_id
  )

  if (!canSetAllowanceIncidences && !hasAllowanceIncidences) return null

  const description = () => {
    if (canSetAllowanceIncidences) {
      return i18n.t('time_off.incidences.description_by_hours')
    } else {
      return i18n.t('time_off.incidences.description_employee_hours')
    }
  }

  const renderAllowanceIncidences = () => {
    const filteredAllowanceIds = filteredAllowances.map((a) => a.attributes.id)
    const filteredIncidences = allowanceIncidences.models.filter((aI) =>
      filteredAllowanceIds.includes(aI.attributes.timeoff_allowance_id)
    )
    // Sort the incidences by their effective date.
    const orderedFilteredIncidences = sortBy(
      filteredIncidences,
      (incidence) => incidence.effectiveOn
    )

    // Render an Item for each incidence, passing timelineItem as a required prop.
    return orderedFilteredIncidences.map((allowanceIncidence) => (
      <Item
        key={allowanceIncidence.id}
        allowanceIncidence={allowanceIncidence}
        employee={employee}
        canSetAllowanceIncidences={canSetAllowanceIncidences}
        timelineItem={timelineItem as PolicyTimelineItem}
        hasManualAdjustmentOptions={hasManualAdjustmentOptions}
      />
    ))
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks

  return (
    <Box paddingTop="s64" paddingBottom="s32" paddingX="s32">
      {open && (
        <AllowanceIncidenceModal
          employee={employee}
          onClose={toggleModal(false)}
          timelineItem={timelineItem as PolicyTimelineItem}
        />
      )}
      <PanesHeader title={i18n.t('time_off.incidences.title')} description={description()} />
      {canSetAllowanceIncidences && (
        <Box paddingBottom="s24">
          <Button
            onClick={toggleModal(true)}
            type="secondary"
            extra={{ 'data-intercom-target': 'timeoff-incidences-add-button' }}
          >
            {i18n.t('time_off.incidences.add')}
          </Button>
        </Box>
      )}
      {hasAllowanceIncidences && <BoxList>{renderAllowanceIncidences()}</BoxList>}
    </Box>
  )
}

export default observer(AllowanceIncidences)
